/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * The widget component for Search accounts on page dashboard
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.15.0
 * @since 1.3.0
 */

import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import get from 'lodash/get';
import invoke from 'lodash/invoke';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { LinkBlock } from '../../../../../../components/common';
import { LayoutStyled } from '../../../../../../components/common/Block/Layout';
import { LoaderLinear } from '../../../../../../components/common/Loader/Loaders';
import { pushGTMAnalytic } from '../../../../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { EGTMWidgetSearchAccountsInteractionType } from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetSearchAccountsOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { KeyboardKeyCodeEnum, KeyboardKeyEnum } from '../../../../../../modules/app/appConstants';
import initTranslationCommon from '../../../../../../modules/internationalization/constants/common';
import initTranslationPageDashboard
    from '../../../../../../modules/internationalization/constants/pages/dashboard/dashboard';
import IPageDashboardTranslation from '../../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { WidgetTitleStyled } from '../../styled/WidgetStyled';
import { makeSelectorWidgetMetaList } from '../../widgetSelectors';
import { EWidgetNames, TWidgetMetaList } from '../../widgetTypes';
import { IWidgetSearchAccountsLoadListPayload } from '../types/widgetSearchAccountsActionTypes';
import { EWidgetSearchAccountsField, IWidgetSearchAccountsItem } from '../types/widgetSearchAccountsTypes';
import {
    widgetSearchAccountsInit,
    widgetSearchAccountsLoadList,
    widgetSearchAccountsSetAccount,
    widgetSearchAccountsSetSearchValue,
} from '../widgetSearchAccountsActions';
import { WIDGET_SEARCH_ACCOUNTS_MIN_SEARCH_LENGTH } from '../widgetSearchAccountsConstants';
import {
    makeSelectWidgetSearchAccountsError,
    makeSelectWidgetSearchAccountsIsLoading,
    makeSelectWidgetSearchAccountsList,
    makeSelectWidgetSearchAccountsSearchValue,
    makeSelectWidgetSearchAccountsSelectedAccount,
} from '../widgetSearchAccountsSelectors';
import {
    WidgetSearchAccountsContainerStyled,
    WidgetSearchAccountsDropdownStyled,
    WidgetSearchAccountsLayoutStyled,
    WidgetSearchAccountsLinkContainer,
    WidgetSearchAccountsLoaderLinearContainer,
    WidgetSearchAccountsMessageContainer,
} from './styled/WidgetSearchAccountsStyled';
import { WidgetSearchAccountsDisplay } from './WidgetSearchAccountsDisplay';
import WidgetSearchAccountsInput from './WidgetSearchAccountsInput';
import WidgetSearchAccountsList from './WidgetSearchAccountsList';

export interface IWidgetSearchAccountsProps {
    translation: IPageDashboardTranslation;
}

export interface IConnectedProps {
    accountList: IWidgetSearchAccountsItem[];
    errorMessage: string;
    isLoading?: boolean;
    searchValue: string;
    selectedAccount: IWidgetSearchAccountsItem;
    widgetMeta: TWidgetMetaList;
}

export interface IDispatchProps {
    loadAccountsList: (payload?: IWidgetSearchAccountsLoadListPayload) => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    setAccount: (payload: IWidgetSearchAccountsItem) => void;
    setSearchValue: (payload: string) => void;
    widgetInit: () => void;
}

export type TWidgetSearchAccountsProps = IWidgetSearchAccountsProps & IConnectedProps & IDispatchProps;

export const WidgetSearchAccounts = ({
                                         errorMessage,
                                         loadAccountsList,
                                         pushGtmAnalytic,
                                         setAccount,
                                         setSearchValue,
                                         translation,
                                         widgetInit,
                                         widgetMeta,
                                         selectedAccount,
                                         isLoading,
                                         accountList,
                                         searchValue,
                                     }: TWidgetSearchAccountsProps) => {

    const t = useMemo(() => translation.widgets.searchAccounts, [translation]);

    const [isOpened, setOpened] = useState(false);
    const [isFirstSearch, setFirstSearch] = useState(true);

    const containerRef: RefObject<HTMLDivElement> = useRef(null);

    const widgetSearchAccountsMeta = widgetMeta.find(widget => widget.name === EWidgetNames.SEARCH_ACCOUNTS);

    /*
    * -------------------------------------------
    *               CALLBACKS
    * -------------------------------------------
    */

    const handleClose = useCallback(() => {
        setOpened(false);
    }, []);

    const handleClickOutside = useCallback((event: MouseEvent): void => {
        if (get(containerRef, 'current') &&
            !invoke(containerRef, 'current.contains', event.target as Node)) {
            handleClose();
        }
    }, [handleClose]);

    const handleClick = useCallback(() => {
        if (!isOpened) {
            setSearchValue('');
            loadAccountsList({ start: true });
            setOpened(true);
        } else {
            handleClose();
        }
        pushGtmAnalytic({
            operation: GTMOperations.widgetSearchAccounts.onClickWidget(isOpened ?
                EGTMWidgetSearchAccountsInteractionType.close : EGTMWidgetSearchAccountsInteractionType.open),
            withAccountType: true,
            withUserRole: true,
            withSusrId: true,
        });
    }, [isOpened, handleClose, setSearchValue, loadAccountsList, pushGtmAnalytic]);

    const handleSelectAccount = useCallback((account: IWidgetSearchAccountsItem) => {
        setAccount(account);
        handleClose();
        pushGtmAnalytic({
            operation: GTMOperations.widgetSearchAccounts.onSelectAccount(account[EWidgetSearchAccountsField.number]),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [handleClose, setAccount, pushGtmAnalytic]);

    const handleFirstSearch = useCallback(() => {
        if (isFirstSearch) {
            pushGtmAnalytic({
                operation: GTMOperations.widgetSearchAccounts.onFirstSearch(),
                withAccountType: true,
                withUserRole: true,
                withSusrId: true,
            });
            setFirstSearch(false);
        }
    }, [isFirstSearch, pushGtmAnalytic]);

    const windowKeyDownHandler = useCallback((event: KeyboardEvent): void => {
        if (event.key === KeyboardKeyEnum.ESCAPE || event.keyCode === KeyboardKeyCodeEnum.ESCAPE) {
            handleClose();
        }
    }, [handleClose]);

    /*
    * -------------------------------------------
    *               EFFECTS
    * -------------------------------------------
    */

    useEffect(() => {
        if (!widgetSearchAccountsMeta) return;
        widgetInit();
    }, [widgetSearchAccountsMeta, widgetInit]);

    useEffect(() => {
        if (!widgetSearchAccountsMeta) return;
        window.addEventListener('click', handleClickOutside, true);
        window.addEventListener('keydown', windowKeyDownHandler, true);
        return () => {
            window.removeEventListener('click', handleClickOutside, true);
            window.removeEventListener('keydown', windowKeyDownHandler, true);
        };
    }, [widgetSearchAccountsMeta, handleClickOutside, windowKeyDownHandler]);

    if (!widgetSearchAccountsMeta) return null;

    return (
        <WidgetSearchAccountsLayoutStyled>
            <LayoutStyled className="mb-2">
                <WidgetTitleStyled>
                    {t.label}
                </WidgetTitleStyled>
            </LayoutStyled>
            <WidgetSearchAccountsContainerStyled
                ref={containerRef}
            >
                <WidgetSearchAccountsDisplay
                    data-component="widgetDisplaySelect"
                    isOpened={isOpened}
                    selectedAccount={selectedAccount}
                    onClick={handleClick}
                />
                {
                    isOpened &&
                    <WidgetSearchAccountsDropdownStyled>
                        <WidgetSearchAccountsInput
                            isOpened={isOpened}
                            translation={translation}
                            onChange={handleFirstSearch}
                        />

                        {
                            isLoading &&
                            accountList.length === 0 &&
                            <WidgetSearchAccountsLoaderLinearContainer
                                data-name="widgetLoaderLinear"
                            >
                                <LoaderLinear maxWidth="70%"/>
                                <LoaderLinear maxWidth="40%"/>
                            </WidgetSearchAccountsLoaderLinearContainer>
                        }

                        {
                            !errorMessage &&
                            accountList.length > 0 &&
                            (!searchValue || searchValue.length >= WIDGET_SEARCH_ACCOUNTS_MIN_SEARCH_LENGTH) &&
                            <WidgetSearchAccountsList
                                data-component="widgetAccountsList"
                                translation={translation}
                                onSelectAccount={handleSelectAccount}
                            />
                        }

                        {
                            !errorMessage &&
                            accountList.length === 0 &&
                            !isLoading &&
                            <WidgetSearchAccountsMessageContainer>
                                {t.notFound}
                            </WidgetSearchAccountsMessageContainer>
                        }

                        {
                            !!errorMessage &&
                            <WidgetSearchAccountsMessageContainer>
                                {errorMessage}
                                <WidgetSearchAccountsLinkContainer
                                    data-name="widgetRepeatLoad"
                                >
                                    <LinkBlock onClick={() => loadAccountsList({ start: true })}>
                                        {t.failedButton}
                                    </LinkBlock>
                                </WidgetSearchAccountsLinkContainer>
                            </WidgetSearchAccountsMessageContainer>
                        }
                    </WidgetSearchAccountsDropdownStyled>
                }
            </WidgetSearchAccountsContainerStyled>
        </WidgetSearchAccountsLayoutStyled>
    );
};

WidgetSearchAccounts.defaultProps = {
    translation: { ...initTranslationCommon, ...initTranslationPageDashboard },
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    accountList: makeSelectWidgetSearchAccountsList(),
    errorMessage: makeSelectWidgetSearchAccountsError(),
    isLoading: makeSelectWidgetSearchAccountsIsLoading(),
    searchValue: makeSelectWidgetSearchAccountsSearchValue(),
    selectedAccount: makeSelectWidgetSearchAccountsSelectedAccount(),
    widgetMeta: makeSelectorWidgetMetaList(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadAccountsList: (payload) =>
        dispatch(widgetSearchAccountsLoadList(payload)),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
    setAccount: (payload) => dispatch(widgetSearchAccountsSetAccount({ account: payload })),
    setSearchValue: (payload) => dispatch(widgetSearchAccountsSetSearchValue({ searchValue: payload })),
    widgetInit: () => dispatch(widgetSearchAccountsInit()),
});

export default connect(mapStateToProps, mapDispatchToProps)(WidgetSearchAccounts);
